import type { Group, Scan } from './types';

export const WHITE = '#ffffff';
export const PINK = '#ff4fa3';
/** Most rows a details panel lists before "and N more". */
export const PANEL_ROWS = 20;
const DAY_MS = 86_400_000;

/** Group scans by cell: one group per location, scans newest first, groups in first-seen order. */
export function groupByCell(scans: Scan[]): Group[] {
  const groups = new Map<string, Group>();
  for (const scan of scans) {
    let g = groups.get(scan.cellId);
    if (!g) {
      g = { cellId: scan.cellId, lat: scan.lat, lng: scan.lng, scans: [], hasMultiPart: false };
      groups.set(scan.cellId, g);
    }
    g.scans.push(scan);
    if (scan.multiPart) g.hasMultiPart = true;
  }
  for (const g of groups.values()) g.scans.sort((a, b) => b.scannedAt - a.scannedAt);
  return [...groups.values()];
}

/** White for a location of ordinary scans, pink where any scan found a part of a multi-part item. */
export function dotColor(group: Group): string {
  return group.hasMultiPart ? PINK : WHITE;
}

/** Draw order: white dots first so pink ones end up on top. */
export function drawOrder(groups: Group[]): Group[] {
  return [...groups.filter((g) => !g.hasMultiPart), ...groups.filter((g) => g.hasMultiPart)];
}

/** What became of a find, for people. Nothing-found scans have no outcome. */
export function outcomeLabel(scan: Scan): string | null {
  if (scan.result === 'nothing') return null;
  switch (scan.outcome) {
    case 'picked_up':
      return 'Picked up';
    case 'left_behind':
      return 'Ignored';
    case 'pending':
      return 'Waiting';
    case 'expired':
      return 'Expired';
    default:
      return null;
  }
}

export interface RowText {
  local: string;
  utc: string;
  /** The player's name, or their id when the name is unknown. */
  player: string;
  playerId: string;
  /** "Nothing found", or the item with its rarity and part. */
  what: string;
  outcome: string | null;
}

const pad = (n: number) => String(n).padStart(2, '0');

export function formatUtc(unix: number): string {
  const d = new Date(unix * 1000);
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())} ${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}:${pad(d.getUTCSeconds())} UTC`;
}

export function formatLocal(unix: number): string {
  return new Date(unix * 1000).toLocaleString();
}

export function rowText(scan: Scan): RowText {
  let what = 'Nothing found';
  if (scan.result === 'found') {
    const item = scan.itemName ?? scan.itemId ?? 'Unknown item';
    const parts = [scan.rarity, scan.partId ? `part ${scan.partId}` : null].filter(Boolean).join(', ');
    what = parts ? `${item} (${parts})` : item;
  }
  return {
    local: formatLocal(scan.scannedAt),
    utc: formatUtc(scan.scannedAt),
    player: scan.playerName ?? scan.playerId,
    playerId: scan.playerId,
    what,
    outcome: outcomeLabel(scan),
  };
}

export function summary(scans: Scan[]): { scans: number; multiPart: number; locations: number } {
  return {
    scans: scans.length,
    multiPart: scans.filter((s) => s.multiPart).length,
    locations: new Set(scans.map((s) => s.cellId)).size,
  };
}

// ---- dates ----------------------------------------------------------------

export function isoDay(date: Date): string {
  return `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}-${pad(date.getUTCDate())}`;
}

/** Day number for a strict `YYYY-MM-DD`, or null for anything else (impossible dates included). */
export function parseDay(text: string): number | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(text);
  if (!m) return null;
  const [y, mo, d] = [Number(m[1]), Number(m[2]), Number(m[3])];
  const ms = Date.UTC(y, mo - 1, d);
  const back = new Date(ms);
  if (back.getUTCFullYear() !== y || back.getUTCMonth() !== mo - 1 || back.getUTCDate() !== d) return null;
  return Math.round(ms / DAY_MS);
}

function shiftDay(today: Date, days: number): string {
  return isoDay(new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate()) + days * DAY_MS));
}

/** The last seven UTC days, today included. */
export function defaultRange(today: Date): { from: string; to: string } {
  return { from: shiftDay(today, -6), to: shiftDay(today, 0) };
}

/** A player's whole history: the 90 UTC days ending today. */
export function playerRange(today: Date): { from: string; to: string } {
  return { from: shiftDay(today, -89), to: shiftDay(today, 0) };
}

/** A message for an unusable range, or null when it is fine. At most 31 days, or 90 for a player. */
export function validateRange(from: string, to: string, forPlayer: boolean): string | null {
  const f = parseDay(from);
  const t = parseDay(to);
  if (f === null || t === null) return 'Enter both dates as YYYY-MM-DD.';
  if (f > t) return 'The range is invalid: From is after To.';
  const max = forPlayer ? 90 : 31;
  if (t - f + 1 > max) return `The range is invalid: it may span at most ${max} days.`;
  return null;
}
